/**
 * B2 chart widgets (PMA-BCAST-008). They reuse the analytics page's own
 * components and utils read-only so the numbers always equal the shared page
 * for the same events:
 *  - stats_panel / top_players mount `MatchAnalytics` (the single place those
 *    numbers are computed) and a scoped stylesheet hides everything but the
 *    requested slice, because the guarded component cannot be given a
 *    "rows" / "slice" prop here;
 *  - shot_map mounts `GoalMouthMap` per side;
 *  - heat_map / area_control mirror `HeatMapSection`'s own calls to
 *    `SoccerFieldHeatMap` / `AreaControlGrid` with the same compute utils.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { MatchEvent } from "../../../store/useMatchLogStore";
import type { Match } from "../../logger/types";
import { MatchAnalytics } from "../../logger/components/molecules/MatchAnalytics";
import GoalMouthMap from "../../logger/components/molecules/GoalMouthMap";
import SoccerFieldHeatMap from "../../logger/components/molecules/SoccerFieldHeatMap";
import AreaControlGrid from "../../logger/components/molecules/AreaControlGrid";
import { resolveMatchTeamColors } from "../../logger/components/molecules/resolveMatchTeamColors";
import {
  computeAreaControlData,
  computeHeatMapData,
  extractHeatPoints,
} from "../../logger/utils/heatMapZones";
import { computeScoreboardClock } from "../clock";

export type TeamOption = "home" | "away" | "both";

export const readTeamOption = (options: Record<string, unknown>): TeamOption =>
  options.team === "home" || options.team === "away" ? options.team : "both";

/** All row keys the stats panel can show (testid minus the `stat-` prefix). */
export const STAT_ROW_KEYS = [
  "xg",
  "possession",
  "accurate-passes",
  "shots",
  "shots-on-target",
  "shots-off-target",
  "shots-blocked",
  "corners",
  "offsides",
  "fouls",
  "ineffective-time",
  "ineffective-time-percent",
  "effective-time",
  "effective-time-percent",
] as const;

/** Scoped CSS that cuts MatchAnalytics down to one slice (see file header). */
export function sliceCss(
  slice: "stats" | "top_players",
  rows: string[],
  team: TeamOption,
  hideHeader = false,
): string {
  const scope = `[data-bcast-slice="${slice}"]`;
  const panel = `${scope} [data-testid="analytics-panel"]`;
  const out: string[] = [
    // Page header and the sibling blocks after the main card.
    `${panel} > :first-child{display:none !important}`,
    `${panel} > :nth-child(n+3){display:none !important}`,
    `${scope} [data-testid="analytics-per-team-time"]{display:none !important}`,
    // Fill the widget rectangle (so a panel's bottom lines up with its
    // neighbours) and centre the rows vertically.
    `${panel}{height:100%}`,
    `${panel} > :nth-child(2){height:100%;margin-top:0 !important;box-sizing:border-box;display:flex;flex-direction:column;justify-content:space-evenly;padding-top:0.5rem !important;padding-bottom:0.5rem !important}`,
  ];
  if (slice === "stats") {
    out.push(
      `${scope} [data-testid="top-players-section"]{display:none !important}`,
      `${scope} [data-testid="analytics-goal-mouth-section"]{display:none !important}`,
    );
    // The card's first row is "Total time", computed from the analytics
    // page's own unreleased clock; the scoreboard already shows the
    // broadcast clock, so this row is always hidden.
    out.push(
      `${panel} > :nth-child(2) > :first-child{display:none !important}`,
    );
    // The identity header (logos, score, possession donuts) duplicates a
    // visible scoreboard widget; the column header row below it keeps the
    // team labels, and the possession row stays in the table.
    if (hideHeader) {
      out.push(
        `${panel} > :nth-child(2) > :nth-child(2){display:none !important}`,
      );
    }
    if (rows.length > 0) {
      const wanted = new Set(rows);
      for (const key of STAT_ROW_KEYS) {
        if (!wanted.has(key))
          out.push(
            `${scope} [data-testid="stat-${key}"]{display:none !important}`,
          );
      }
    }
  } else {
    out.push(
      `${panel} > :nth-child(2) > :not([data-testid="top-players-section"]){display:none !important}`,
    );
    if (team !== "both") {
      const hide = team === "home" ? "away" : "home";
      out.push(
        `${scope} [data-testid="top-players-${hide}"]{display:none !important}`,
      );
    }
  }
  return out.join("\n");
}

interface Common {
  match: Match;
  events: MatchEvent[];
  options: Record<string, unknown>;
}

const useLoggerT = () => {
  const { t, i18n } = useTranslation("logger");
  return {
    t: t as unknown as TFunction<"logger">,
    tLoose: t as unknown as (key: string, fallback?: string) => string,
    language: (i18n.language === "es" ? "es" : "en") as "en" | "es",
  };
};

function AnalyticsSlice({
  slice,
  match,
  events,
  rows,
  team,
  hideHeader,
  getPlaybackNowMs,
}: {
  slice: "stats" | "top_players";
  match: Match;
  events: MatchEvent[];
  rows: string[];
  team: TeamOption;
  hideHeader: boolean;
  getPlaybackNowMs: () => number;
}) {
  const { tLoose } = useLoggerT();
  const css = useMemo(
    () => sliceCss(slice, rows, team, hideHeader),
    [slice, rows, team, hideHeader],
  );
  const nowMs = getPlaybackNowMs();
  const { clock, phase } = computeScoreboardClock(match, events, nowMs);
  return (
    <div
      data-bcast-slice={slice}
      data-testid={`broadcast-slice-${slice}`}
      style={{ width: "100%", height: "100%", overflow: "hidden" }}
    >
      <style>{css}</style>
      <MatchAnalytics
        match={match}
        events={events}
        effectiveTime={0}
        hidePlayerHeatmaps
        hideExportControls
        hidePerTeamTimeTable
        currentClockSeconds={clock.mainSeconds + clock.addedSeconds}
        matchFinished={phase === "COMPLETED" || phase === "PENALTIES"}
        t={tLoose}
      />
    </div>
  );
}

export function StatsPanel({
  match,
  events,
  options,
  hideHeader = false,
  getPlaybackNowMs,
}: Common & { hideHeader?: boolean; getPlaybackNowMs: () => number }) {
  const rows = Array.isArray(options.rows)
    ? (options.rows as unknown[]).filter(
        (r): r is string => typeof r === "string",
      )
    : [];
  return (
    <AnalyticsSlice
      slice="stats"
      match={match}
      events={events}
      rows={rows}
      team="both"
      hideHeader={hideHeader}
      getPlaybackNowMs={getPlaybackNowMs}
    />
  );
}

export function TopPlayers({
  match,
  events,
  options,
  getPlaybackNowMs,
}: Common & { getPlaybackNowMs: () => number }) {
  return (
    <AnalyticsSlice
      slice="top_players"
      match={match}
      events={events}
      rows={[]}
      team={readTeamOption(options)}
      hideHeader={false}
      getPlaybackNowMs={getPlaybackNowMs}
    />
  );
}

export function ShotMap({ match, events, options }: Common) {
  const { t } = useLoggerT();
  const team = readTeamOption(options);
  return (
    <div
      data-testid="broadcast-shot-map"
      style={{
        display: "grid",
        gridTemplateColumns: team === "both" ? "1fr 1fr" : "1fr",
        gap: "1em",
        width: "100%",
        height: "100%",
        alignContent: "center",
        color: "#e2e8f0",
      }}
    >
      {team !== "away" && (
        <GoalMouthMap
          events={events}
          team={match.home_team}
          side="home"
          hidePlayerSelector
          t={t}
        />
      )}
      {team !== "home" && (
        <GoalMouthMap
          events={events}
          team={match.away_team}
          side="away"
          hidePlayerSelector
          t={t}
        />
      )}
    </div>
  );
}

export function HeatMap({ match, events, options }: Common) {
  const { tLoose } = useLoggerT();
  const team = readTeamOption(options);
  const homeId = match.home_team.id;
  const awayId = match.away_team.id;
  const homeData = useMemo(
    () => computeHeatMapData(events, homeId),
    [events, homeId],
  );
  const homePoints = useMemo(
    () => extractHeatPoints(events, homeId),
    [events, homeId],
  );
  const awayData = useMemo(
    () => computeHeatMapData(events, awayId),
    [events, awayId],
  );
  const awayPoints = useMemo(
    () => extractHeatPoints(events, awayId),
    [events, awayId],
  );
  return (
    <div
      data-testid="broadcast-heat-map"
      style={{
        display: "grid",
        gridTemplateColumns: team === "both" ? "1fr 1fr" : "1fr",
        gap: "1em",
        width: "100%",
        height: "100%",
        alignContent: "center",
      }}
    >
      {team !== "away" && (
        <SoccerFieldHeatMap
          data={homeData}
          points={homePoints}
          title={match.home_team.short_name || match.home_team.name}
          accentColor="#10b981"
          team={match.home_team}
          side="home"
          pitchArrows
          rowLayout
          t={tLoose}
          data-testid="heatmap-home"
        />
      )}
      {team !== "home" && (
        <SoccerFieldHeatMap
          data={awayData}
          points={awayPoints}
          title={match.away_team.short_name || match.away_team.name}
          accentColor="#6366f1"
          team={match.away_team}
          side="away"
          pitchArrows
          rowLayout
          t={tLoose}
          data-testid="heatmap-away"
        />
      )}
    </div>
  );
}

export function AreaControl({ match, events }: Common) {
  const { tLoose } = useLoggerT();
  const data = useMemo(
    () =>
      computeAreaControlData(events, match.home_team.id, match.away_team.id),
    [events, match.home_team.id, match.away_team.id],
  );
  const { home, away } = useMemo(
    () => resolveMatchTeamColors(match),
    [match.home_team, match.away_team],
  );
  return (
    <div
      data-testid="broadcast-area-control"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AreaControlGrid
        data={data}
        homeTeamName={match.home_team.short_name || match.home_team.name}
        awayTeamName={match.away_team.short_name || match.away_team.name}
        homeColor={home}
        awayColor={away}
        homeTeam={match.home_team}
        awayTeam={match.away_team}
        t={tLoose}
        data-testid="area-control-grid"
      />
    </div>
  );
}
